/**
 * Tax Calculations Service
 * Calcola imposte di acquisto immobile e costi notarili secondo normativa italiana
 */

import type { Notaio, Project } from '../types';

export interface TaxBreakdown {
  // Imposte di acquisto
  impostaRegistro: number;
  impostaIpotecaria: number;
  impostaCatastale: number;
  iva: number;
  impostaSostitutiva: number;

  // Totale imposte
  totaleImposte: number;

  // Costi notarili
  onorarioCompravendita: number;
  onorarioMutuo: number;
  visureVerifiche: number;
  bolliDiritti: number;
  totaleCostiNotarili: number;

  // Commissioni
  commissioneAgenzia: number;

  // Totale generale
  totaleGenerale: number;

  /**
   * true quando gli importi vengono dai valori consuntivi salvati nella scheda
   * Notaio, false quando sono stimati dalle aliquote di legge.
   */
  consuntivo: boolean;
}

/**
 * Parametri fiscali reali del progetto.
 *
 * Il tipo `Notaio` porta con sé alcuni campi "frontend only" (isPrimaCasa,
 * percentualeAgenzia, catastale, conMutuo, onorarioCompravendita…) che il
 * backend non popola mai: sono rimasti da una versione precedente del modello.
 * Leggerli direttamente faceva cadere tutto sui valori di default, producendo un
 * breakdown scollegato dai dati reali. Qui i parametri vengono risolti dalle
 * fonti che il database valorizza davvero — scheda Notaio, Immobile e
 * finanziamenti — con i vecchi campi come ultimo fallback.
 */
export function risolviParametriFiscali(project: Project, notaio: Notaio) {
  const immobile = project.immobile ?? null;
  const num = (v: string | number | null | undefined): number => {
    if (v === null || v === undefined) return 0;
    const n = typeof v === 'number' ? v : parseFloat(v);
    return Number.isFinite(n) ? n : 0;
  };

  const prezzoImmobile =
    num(notaio.prezzoAcquisto) || num(immobile?.prezzoAcquisto) || project.prezzoImmobile || 0;

  const primaCasa =
    notaio.primaCasa ?? immobile?.primaCasa ?? notaio.isPrimaCasa ?? true;

  const venditoreImpresa = immobile?.venditoreImpresa ?? notaio.venditoreImpresa ?? false;

  const renditaCatastale = num(immobile?.renditaCatastale) || notaio.catastale || 0;

  const percentualeAgenzia =
    num(immobile?.percentualeAgenzia) || notaio.percentualeAgenzia || 0;

  // Il mutuo c'è se è censito tra i finanziamenti: più affidabile di un flag.
  const conMutuo =
    (project.finanziamenti ?? []).some((f) => f.tipo === 'mutuo') || notaio.conMutuo || false;

  return {
    prezzoImmobile,
    primaCasa,
    venditoreImpresa,
    renditaCatastale,
    percentualeAgenzia,
    conMutuo,
  };
}

/**
 * Calcola la base imponibile per le imposte
 * Prima casa: valore catastale rivalutato (rendita * 1.05 * 110)
 * Seconda casa: valore catastale rivalutato (rendita * 1.05 * 120)
 */
export function calcolaBaseImponibile(
  prezzoImmobile: number,
  renditaCatastale: number,
  isPrimaCasa: boolean
): number {
  if (renditaCatastale <= 0) {
    // Se non c'è rendita catastale, usa il prezzo di acquisto
    return prezzoImmobile;
  }

  // Rivalutazione rendita catastale
  const rendRivalutata = renditaCatastale * 1.05;

  // Moltiplicatore per calcolo valore catastale
  const moltiplicatore = isPrimaCasa ? 110 : 120;

  const valoreCatastale = rendRivalutata * moltiplicatore;

  // Per privati si usa il valore catastale (più conveniente)
  // Per imprese si usa il prezzo di acquisto
  return valoreCatastale;
}

/**
 * Calcola le imposte di acquisto da privato
 */
export function calcolaImpostePrivato(
  prezzoImmobile: number,
  renditaCatastale: number,
  isPrimaCasa: boolean,
  conMutuo: boolean
): Pick<TaxBreakdown, 'impostaRegistro' | 'impostaIpotecaria' | 'impostaCatastale' | 'iva' | 'impostaSostitutiva'> {
  const baseImponibile = calcolaBaseImponibile(prezzoImmobile, renditaCatastale, isPrimaCasa);

  // Aliquote imposta registro
  const aliquotaRegistro = isPrimaCasa ? 0.02 : 0.09;
  let impostaRegistro = baseImponibile * aliquotaRegistro;

  // Minimo imposta registro: 1.000 EUR
  impostaRegistro = Math.max(impostaRegistro, 1000);

  // Imposte ipotecaria e catastale fisse (acquisto da privato)
  const impostaIpotecaria = 50;
  const impostaCatastale = 50;

  // Nessuna IVA per acquisto da privato
  const iva = 0;

  // Imposta sostitutiva sul mutuo (0.25% prima casa, 2% seconda casa)
  let impostaSostitutiva = 0;
  if (conMutuo) {
    const aliquotaSostitutiva = isPrimaCasa ? 0.0025 : 0.02;
    // L'imposta sostitutiva si calcola sull'importo del mutuo
    // Approssimazione: 80% del prezzo immobile
    impostaSostitutiva = prezzoImmobile * 0.8 * aliquotaSostitutiva;
  }

  return {
    impostaRegistro: Math.round(impostaRegistro * 100) / 100,
    impostaIpotecaria,
    impostaCatastale,
    iva,
    impostaSostitutiva: Math.round(impostaSostitutiva * 100) / 100
  };
}

/**
 * Calcola le imposte di acquisto da impresa
 */
export function calcolaImposteImpresa(
  prezzoImmobile: number,
  isPrimaCasa: boolean,
  conMutuo: boolean
): Pick<TaxBreakdown, 'impostaRegistro' | 'impostaIpotecaria' | 'impostaCatastale' | 'iva' | 'impostaSostitutiva'> {
  // Imposte registro, ipotecaria, catastale fisse (acquisto da impresa)
  const impostaRegistro = 200;
  const impostaIpotecaria = 200;
  const impostaCatastale = 200;

  // IVA (4% prima casa, 10% seconda casa)
  const aliquotaIva = isPrimaCasa ? 0.04 : 0.10;
  const iva = prezzoImmobile * aliquotaIva;

  // Imposta sostitutiva sul mutuo
  let impostaSostitutiva = 0;
  if (conMutuo) {
    const aliquotaSostitutiva = isPrimaCasa ? 0.0025 : 0.02;
    impostaSostitutiva = prezzoImmobile * 0.8 * aliquotaSostitutiva;
  }

  return {
    impostaRegistro,
    impostaIpotecaria,
    impostaCatastale,
    iva: Math.round(iva * 100) / 100,
    impostaSostitutiva: Math.round(impostaSostitutiva * 100) / 100
  };
}

/**
 * Calcola i costi notarili
 */
export function calcolaCostiNotarili(
  notaio: Notaio
): Pick<TaxBreakdown, 'onorarioCompravendita' | 'onorarioMutuo' | 'visureVerifiche' | 'bolliDiritti' | 'totaleCostiNotarili'> {
  const onorarioCompravendita = notaio.onorarioCompravendita || 1200;
  const onorarioMutuo = notaio.conMutuo ? (notaio.onorarioMutuo || 650) : 0;
  const visureVerifiche = notaio.visureVerifiche || 150;
  const bolliDiritti = notaio.bolliDiritti || 230;

  const totaleCostiNotarili = onorarioCompravendita + onorarioMutuo + visureVerifiche + bolliDiritti;

  return {
    onorarioCompravendita,
    onorarioMutuo,
    visureVerifiche,
    bolliDiritti,
    totaleCostiNotarili
  };
}

/**
 * Calcola la commissione agenzia
 */
export function calcolaCommissioneAgenzia(
  prezzoImmobile: number,
  percentualeAgenzia: number
): number {
  const commissione = prezzoImmobile * (percentualeAgenzia / 100);
  // IVA 22% sulla commissione
  const commissioneConIva = commissione * 1.22;
  return Math.round(commissioneConIva * 100) / 100;
}

/**
 * Calcola il breakdown completo delle tasse e costi accessori
 */
export function calcolaTaxBreakdown(
  project: Project,
  notaio: Notaio
): TaxBreakdown {
  const par = risolviParametriFiscali(project, notaio);
  const num = (v: string | number | null | undefined): number => {
    if (v === null || v === undefined || v === '') return 0;
    const n = typeof v === 'number' ? v : parseFloat(v);
    return Number.isFinite(n) ? n : 0;
  };

  // Stima dalle aliquote di legge
  const stimate = par.venditoreImpresa
    ? calcolaImposteImpresa(par.prezzoImmobile, par.primaCasa, par.conMutuo)
    : calcolaImpostePrivato(par.prezzoImmobile, par.renditaCatastale, par.primaCasa, par.conMutuo);

  // Consuntivo: se la scheda Notaio riporta gli importi dell'atto, quelli
  // vincono sulla stima. Un rogito già fatto ha numeri certi, non presunti.
  const registroReale = num(notaio.impostaRegistro);
  const haConsuntivo = registroReale > 0;

  const imposte = haConsuntivo
    ? {
        impostaRegistro: registroReale,
        impostaIpotecaria: num(notaio.impostaIpotecaria) || stimate.impostaIpotecaria,
        impostaCatastale: num(notaio.impostaCatastale) || stimate.impostaCatastale,
        iva: num(notaio.iva),
        // L'imposta sostitutiva sul mutuo non è nella scheda: resta stimata.
        impostaSostitutiva: stimate.impostaSostitutiva,
      }
    : stimate;

  const totaleImposte =
    imposte.impostaRegistro +
    imposte.impostaIpotecaria +
    imposte.impostaCatastale +
    imposte.iva +
    imposte.impostaSostitutiva;

  // Costi notarili: onorario e spese accessorie reali quando presenti
  const onorarioReale = num(notaio.onorarioNotaio);
  const speseAccessorieReali = num(notaio.speseAccessorie);

  const costiNotarili = haConsuntivo
    ? {
        onorarioCompravendita: onorarioReale || calcolaCostiNotarili(notaio).onorarioCompravendita,
        // Nel consuntivo l'atto di mutuo è già dentro le spese accessorie.
        onorarioMutuo: 0,
        visureVerifiche: 0,
        bolliDiritti: speseAccessorieReali,
        totaleCostiNotarili: (onorarioReale || 0) + speseAccessorieReali,
      }
    : calcolaCostiNotarili(notaio);

  const commissioneAgenzia = calcolaCommissioneAgenzia(
    par.prezzoImmobile,
    par.percentualeAgenzia
  );

  const totaleGenerale = totaleImposte + costiNotarili.totaleCostiNotarili + commissioneAgenzia;

  return {
    ...imposte,
    totaleImposte: Math.round(totaleImposte * 100) / 100,
    ...costiNotarili,
    commissioneAgenzia,
    totaleGenerale: Math.round(totaleGenerale * 100) / 100,
    consuntivo: haConsuntivo,
  };
}

/**
 * Calcola il totale costi notaio/tasse per la dashboard
 */
export function calcolaTotaleNotaioTasse(
  project: Project,
  notaio: Notaio | null | undefined
): number {
  if (!notaio) return 0;

  const breakdown = calcolaTaxBreakdown(project, notaio);
  return breakdown.totaleGenerale;
}
